import { supabase } from '../lib/supabase';

export interface DetectedDevice {
  deviceId: string;
  kind: string;
  label: string;
}

export interface DeviceCheckResult {
  hasExternalInput: boolean;
  devices: DetectedDevice[];
  permissionGranted: boolean;
}

export interface DeviceCheckPayload {
  userId: string;
  songId: string;
  recordingType: 'audio' | 'video';
  headsetDetected: boolean;
  detectedDevices: DetectedDevice[];
  userProceededWithoutHeadset: boolean;
  overrideDevice?: DetectedDevice;
}

export interface DeviceOverridePayload {
  userId: string;
  deviceLabel: string;
  deviceIdHash: string;
  recordingDeviceCheckId: string;
  recordingId?: string;
}

const VIRTUAL_DEVICE_KEYWORDS = [
  'virtual',
  'teams',
  'zoom',
  'discord',
  'skype',
  'webex',
  'meet',
  'aggregate',
  'soundflower',
  'blackhole',
  'loopback',
  'voicemeeter',
  'cable',
  'vb-audio',
  'ndi',
];

const EXCLUDED_KEYWORDS = [
  'built-in',
  'internal',
  'default',
  'macbook',
  'imac',
  'ipad',
  'iphone',
  'surface',
  'laptop',
  'monitor',
];

const EXTERNAL_INPUT_KEYWORDS = [
  'headset',
  'headphone',
  'earphone',
  'earbuds',
  'earbud',
  'airpods',
  'beats',
  'bluetooth',
  'usb mic',
  'usb audio',
  'usb headset',
  'external mic',
  'external microphone',
  'wired',
  'logitech',
  'jabra',
  'plantronics',
  'sennheiser',
  'bose',
  'hyperx',
  'razer',
  'corsair',
  'steelseries',
  'blue yeti',
  'blue snowball',
  'rode',
  'at2020',
  'audio-technica',
  'shure',
];

function isDefinitelyBuiltIn(label: string): boolean {
  return EXCLUDED_KEYWORDS.some(kw => label.includes(kw));
}

function isDefinitelyVirtual(label: string): boolean {
  return VIRTUAL_DEVICE_KEYWORDS.some(kw => label.includes(kw));
}

function isExternalInput(device: MediaDeviceInfo): boolean {
  if (device.kind !== 'audioinput') return false;
  const label = device.label.toLowerCase();
  if (!label) return false;

  if (isDefinitelyVirtual(label)) return false;

  if (label.includes('external')) return true;
  if (EXTERNAL_INPUT_KEYWORDS.some(kw => label.includes(kw))) return true;

  if (isDefinitelyBuiltIn(label)) return false;

  return false;
}

export async function detectAudioDevices(): Promise<DeviceCheckResult> {
  let permissionGranted = false;
  let stream: MediaStream | null = null;

  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    permissionGranted = true;
  } catch {
    return { hasExternalInput: false, devices: [], permissionGranted: false };
  } finally {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
    }
  }

  const allDevices = await navigator.mediaDevices.enumerateDevices();
  const audioInputs = allDevices.filter(d => d.kind === 'audioinput');

  const devices: DetectedDevice[] = audioInputs.map(d => ({
    deviceId: d.deviceId,
    kind: d.kind,
    label: d.label || `Microphone ${d.deviceId.slice(0, 6)}`,
  }));

  const hasExternalInput = audioInputs.some(d => isExternalInput(d));

  return { hasExternalInput, devices, permissionGranted };
}

export async function getCurrentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export async function logDeviceCheck(payload: DeviceCheckPayload): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('recording_device_checks')
      .insert({
        user_id: payload.userId,
        song_id: payload.songId,
        recording_type: payload.recordingType,
        headset_detected: payload.headsetDetected,
        detected_devices: payload.detectedDevices,
        user_proceeded_without_headset: payload.userProceededWithoutHeadset,
      })
      .select('id')
      .maybeSingle();

    if (error) throw error;
    return data?.id ?? null;
  } catch (error) {
    console.error('DeviceService: failed to log device check', error);
    return null;
  }
}

export async function linkDeviceCheckToRecording(
  checkId: string,
  recordingId: string,
): Promise<void> {
  try {
    const { error } = await supabase
      .from('recording_device_checks')
      .update({ recording_id: recordingId })
      .eq('id', checkId);

    if (error) throw error;
  } catch (error) {
    console.error('DeviceService: failed to link device check to recording', error);
  }
}

export async function didUserProceedWithoutHeadset(checkId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('recording_device_checks')
      .select('user_proceeded_without_headset')
      .eq('id', checkId)
      .maybeSingle();

    if (error) throw error;
    return data?.user_proceeded_without_headset ?? false;
  } catch (error) {
    console.error('DeviceService: failed to read device check', error);
    return false;
  }
}

export async function flagRecordingAudioQuality(recordingId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('recordings')
      .update({ flagged_audio_quality: true })
      .eq('id', recordingId);

    if (error) throw error;
  } catch (error) {
    console.error('DeviceService: failed to flag recording audio quality', error);
  }
}

export async function hashDeviceId(deviceId: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(deviceId);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
  } catch {
    return deviceId.slice(0, 16);
  }
}

export async function logDeviceOverride(payload: DeviceOverridePayload): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('device_name_overrides')
      .insert({
        user_id: payload.userId,
        device_label: payload.deviceLabel,
        device_id_hash: payload.deviceIdHash,
        recording_device_check_id: payload.recordingDeviceCheckId,
        recording_id: payload.recordingId ?? null,
      })
      .select('id')
      .maybeSingle();

    if (error) throw error;
    return data?.id ?? null;
  } catch (error) {
    console.error('DeviceService: failed to log device override', error);
    return null;
  }
}

export async function linkDeviceOverrideToRecording(
  overrideId: string,
  recordingId: string,
): Promise<void> {
  try {
    const { error } = await supabase
      .from('device_name_overrides')
      .update({ recording_id: recordingId })
      .eq('id', overrideId);

    if (error) throw error;
  } catch (error) {
    console.error('DeviceService: failed to link device override to recording', error);
  }
}
